import { bindsPair, subtitleWords } from './typography.js';
import type { Word } from './types.js';

export interface Segment {
  src_in: number;
  src_out: number;
}

/**
 * Пересчёт времени между исходным файлом и таймлайном готового ролика.
 * После вырезания пауз времена расходятся, а субтитры и врезки живут в
 * таймлайне, поэтому каждое слово транскрипта надо перенести.
 */
export class TimelineMap {
  readonly segments: Segment[];
  /** Смещение начала каждого сегмента на таймлайне. */
  private readonly offsets: number[];
  readonly totalDuration: number;
  /** Ускорение готового ролика: секунда источника длится 1/speed секунды таймлайна. */
  readonly speed: number;

  constructor(segments: Segment[], speed = 1) {
    this.segments = segments.map((s) => ({ src_in: s.src_in, src_out: s.src_out }));
    this.speed = speed > 0 ? speed : 1;
    this.offsets = [];
    let acc = 0;
    for (const s of this.segments) {
      this.offsets.push(acc);
      acc += Math.max(0, s.src_out - s.src_in) / this.speed;
    }
    this.totalDuration = acc;
  }

  /** Время источника в время таймлайна. null, если попало в вырезанный кусок. */
  srcToTl(t: number): number | null {
    for (let i = 0; i < this.segments.length; i++) {
      const s = this.segments[i]!;
      if (t >= s.src_in && t <= s.src_out) return this.offsets[i]! + (t - s.src_in) / this.speed;
    }
    return null;
  }

  /** Ближайшее время таймлайна: время из выреза прижимается к краю сегмента. */
  srcToTlClamped(t: number): number {
    const exact = this.srcToTl(t);
    if (exact !== null) return exact;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < this.segments.length; i++) {
      const s = this.segments[i]!;
      for (const [edge, tl] of [
        [s.src_in, this.offsets[i]!],
        [s.src_out, this.offsets[i]! + (s.src_out - s.src_in) / this.speed],
      ] as const) {
        const d = Math.abs(t - edge);
        if (d < bestDist) {
          bestDist = d;
          best = tl;
        }
      }
    }
    return best;
  }

  /** Время таймлайна в время источника. */
  tlToSrc(t: number): number {
    const clamped = Math.max(0, Math.min(t, this.totalDuration));
    for (let i = 0; i < this.segments.length; i++) {
      const s = this.segments[i]!;
      const start = this.offsets[i]!;
      const end = start + (s.src_out - s.src_in) / this.speed;
      if (clamped >= start && clamped <= end) return s.src_in + (clamped - start) * this.speed;
    }
    const last = this.segments[this.segments.length - 1];
    return last ? last.src_out : 0;
  }
}

/** Слово, перенесённое на таймлайн ролика. */
export interface TimedWord extends Word {
  tlStart: number;
  tlEnd: number;
}

/**
 * Переносит слова на таймлайн. Слова целиком внутри вырезанных кусков
 * отбрасываются, слова на границе подрезаются по сегменту.
 */
export function remapWords(words: Word[], map: TimelineMap): TimedWord[] {
  const out: TimedWord[] = [];
  for (const w of words) {
    for (let i = 0; i < map.segments.length; i++) {
      const s = map.segments[i]!;
      const overlapStart = Math.max(w.start, s.src_in);
      const overlapEnd = Math.min(w.end, s.src_out);
      if (overlapEnd - overlapStart <= 0.01) continue;
      const tlStart = map.srcToTlClamped(overlapStart);
      const tlEnd = map.srcToTlClamped(overlapEnd);
      if (tlEnd - tlStart <= 0.01) continue;
      out.push({ ...w, tlStart, tlEnd });
      break;
    }
  }
  return out.sort((a, b) => a.tlStart - b.tlStart);
}

export interface Phrase {
  words: TimedWord[];
  start: number;
  end: number;
  text: string;
}

export interface PhraseOptions {
  /** Максимум знаков в строке субтитров. */
  maxChars?: number;
  /** Мягкий лимит: не разрываем предлоги и числовые связки. */
  maxWords?: number;
  maxDurationS?: number;
  /** Пауза между словами, по которой рвём фразу. */
  gapS?: number;
}

/** Группирует слова во фразы под субтитры: по паузам, длине и знакам препинания. */
export function groupIntoPhrases(words: TimedWord[], options: PhraseOptions = {}): Phrase[] {
  const maxChars = options.maxChars ?? 42;
  const maxDurationS = options.maxDurationS ?? 3.2;
  const gapS = options.gapS ?? 0.35;

  const phrases: Phrase[] = [];
  let current: TimedWord[] = [];

  const flush = () => {
    if (current.length === 0) return;
    const text = current.map((w) => w.text).join(' ').replace(/\s+/g, ' ').trim();
    phrases.push({
      words: current,
      start: current[0]!.tlStart,
      end: current[current.length - 1]!.tlEnd,
      text,
    });
    current = [];
  };

  for (const w of subtitleWords(words)) {
    const prev = current[current.length - 1];
    const gap = prev ? w.tlStart - prev.tlEnd : 0;
    const chars = current.reduce((n, x) => n + x.text.length + 1, 0) + w.text.length;
    const duration = prev ? w.tlEnd - current[0]!.tlStart : 0;
    if (prev && (gap >= gapS || chars > maxChars || duration > maxDurationS || current.length >= (options.maxWords ?? Infinity))) {
      let split = current.length;
      while (split > 0 && bindsPair(current[split - 1]!.text, current[split]?.text ?? w.text)) split--;
      const carry = current.slice(split);
      current = current.slice(0, split);
      flush();
      current = carry;
    }
    current.push(w);
    if (/[.!?…]$/.test(w.text)) flush();
  }
  flush();
  return phrases;
}

/**
 * Границы слов на таймлайне: паузы между словами, куда безопасно ставить
 * врезку, не разрезая слово пополам.
 */
export function wordBoundaries(words: TimedWord[]): number[] {
  const points: number[] = [];
  if (words.length === 0) return points;
  points.push(words[0]!.tlStart);
  for (let i = 0; i < words.length - 1; i++) {
    const a = words[i]!;
    const b = words[i + 1]!;
    points.push((a.tlEnd + b.tlStart) / 2);
  }
  points.push(words[words.length - 1]!.tlEnd);
  return points;
}

/** Прижимает время к ближайшей границе слова, если та не дальше tolerance. */
export function snapToBoundary(t: number, boundaries: number[], tolerance = 0.4): number {
  let best = t;
  let bestDist = tolerance;
  for (const b of boundaries) {
    const d = Math.abs(b - t);
    if (d < bestDist) {
      bestDist = d;
      best = b;
    }
  }
  return best;
}
